export const PICTOGRAM_GRID = 40
export const PICTOGRAM_CORNER = 8

export const PICTOGRAM_CATEGORIES = [
  'entrances',
  'conveyances',
  'parking',
  'ppe-offices',
  'craft-welfare',
  'brands',
  'deliveries-construction',
  'materials-storage',
  'information',
  'emergency',
] as const

export type PictogramCategoryId = (typeof PICTOGRAM_CATEGORIES)[number]

// Stored in data as `pictogram:<slug>`: slugs are only ever added, never renamed or removed.
// `kinds` are the location types an icon belongs to, and can change freely: a type's icons are
// the ones naming it, in this order, and an Information Point type is drawn by default with the
// first. Any type may still pick any icon.
export const PICTOGRAMS = [
  { slug: 'building', category: 'information', kinds: ['building'] },
  { slug: 'level', category: 'information', kinds: ['level'] },
  { slug: 'zone', category: 'information', kinds: ['zone'] },
  { slug: 'area', category: 'information', kinds: ['area'] },
  { slug: 'route', category: 'entrances', kinds: ['route'] },
  { slug: 'door', category: 'entrances', kinds: ['interior_door'] },
  { slug: 'gate', category: 'entrances', kinds: ['gate'] },
  { slug: 'shaft', category: 'conveyances', kinds: ['shaft'] },
  { slug: 'entrance', category: 'entrances', kinds: ['access'] },
  { slug: 'exit', category: 'entrances', kinds: ['exit'] },
  { slug: 'access-a', category: 'entrances', kinds: ['access'] },
  { slug: 'access-b', category: 'entrances', kinds: ['access'] },
  { slug: 'access-c', category: 'entrances', kinds: ['access'] },
  { slug: 'access-d', category: 'entrances', kinds: ['access'] },
  { slug: 'access-e', category: 'entrances', kinds: ['access'] },
  { slug: 'access-f', category: 'entrances', kinds: ['access'] },
  {
    slug: 'no-access-1',
    category: 'entrances',
    kinds: ['restricted_area', 'traffic_control'],
  },
  { slug: 'no-access-2', category: 'entrances', kinds: ['restricted_area'] },
  { slug: 'no-access-3', category: 'entrances', kinds: ['restricted_area'] },
  { slug: 'pedestrian-walkway', category: 'conveyances', kinds: ['walkway'] },
  { slug: 'elevator', category: 'conveyances', kinds: ['elevator'] },
  {
    slug: 'stairs',
    category: 'conveyances',
    kinds: ['stairway', 'elevated_access'],
  },
  { slug: 'parking', category: 'parking', kinds: ['parking'] },
  { slug: 'bike-parking', category: 'parking', kinds: ['parking'] },
  { slug: 'buggy-parking', category: 'parking', kinds: ['parking'] },
  { slug: 'safety-helmet', category: 'ppe-offices', kinds: ['ppe_zone'] },
  { slug: 'hi-vis-apparel', category: 'ppe-offices', kinds: ['ppe_zone'] },
  { slug: 'safety-gloves', category: 'ppe-offices', kinds: ['ppe_zone'] },
  { slug: 'safety-boots', category: 'ppe-offices', kinds: ['ppe_zone'] },
  { slug: 'not-required-all', category: 'ppe-offices', kinds: ['ppe_zone'] },
  {
    slug: 'not-required-apparel',
    category: 'ppe-offices',
    kinds: ['ppe_zone'],
  },
  { slug: 'not-required-helmet', category: 'ppe-offices', kinds: ['ppe_zone'] },
  { slug: 'not-required-gloves', category: 'ppe-offices', kinds: ['ppe_zone'] },
  { slug: 'not-required-boots', category: 'ppe-offices', kinds: ['ppe_zone'] },
  {
    slug: 'visitor-check-in',
    category: 'ppe-offices',
    kinds: ['site_office', 'security_checkpoint'],
  },
  { slug: 'admin-office', category: 'ppe-offices', kinds: ['site_office'] },
  {
    slug: 'electrical-room',
    category: 'ppe-offices',
    kinds: ['electrical_room'],
  },
  { slug: 'restrooms', category: 'craft-welfare', kinds: ['restrooms'] },
  { slug: 'restrooms-men', category: 'craft-welfare', kinds: ['restrooms'] },
  { slug: 'restrooms-women', category: 'craft-welfare', kinds: ['restrooms'] },
  { slug: 'break-area', category: 'craft-welfare', kinds: ['break_area'] },
  {
    slug: 'hydration-station',
    category: 'craft-welfare',
    kinds: ['hydration_station'],
  },
  { slug: 'smoking-area', category: 'craft-welfare', kinds: ['smoking_area'] },
  { slug: 'canteen', category: 'craft-welfare', kinds: ['canteen'] },
  { slug: 'break', category: 'craft-welfare', kinds: ['break_area'] },
  { slug: 'wash-area', category: 'craft-welfare', kinds: ['handwash_station'] },
  { slug: 'dumpster', category: 'craft-welfare', kinds: ['waste'] },
  { slug: 'brand-united-rentals', category: 'brands', kinds: [] },
  { slug: 'brand-sunbelt', category: 'brands', kinds: [] },
  { slug: 'brand-herc-rentals', category: 'brands', kinds: [] },
  { slug: 'brand-equipmentshare', category: 'brands', kinds: [] },
  { slug: 'brand-feco', category: 'brands', kinds: [] },
  { slug: 'brand-oes', category: 'brands', kinds: [] },
  {
    slug: 'deliveries',
    category: 'deliveries-construction',
    kinds: ['zone'],
  },
  {
    slug: 'construction-vehicles',
    category: 'deliveries-construction',
    kinds: ['zone'],
  },
  {
    slug: 'loading-zone',
    category: 'deliveries-construction',
    kinds: ['loading'],
  },
  { slug: 'trailer', category: 'deliveries-construction', kinds: ['trailer'] },
  {
    slug: 'security-checkpoint',
    category: 'deliveries-construction',
    kinds: ['security_checkpoint'],
  },
  {
    slug: 'temporary-water',
    category: 'deliveries-construction',
    kinds: ['utilities'],
  },
  {
    slug: 'equipment-storage',
    category: 'materials-storage',
    kinds: ['staging'],
  },
  { slug: 'material-boxes', category: 'materials-storage', kinds: ['staging'] },
  { slug: 'material-wood', category: 'materials-storage', kinds: ['staging'] },
  {
    slug: 'material-steel-1',
    category: 'materials-storage',
    kinds: ['staging'],
  },
  {
    slug: 'material-plumbing',
    category: 'materials-storage',
    kinds: ['staging'],
  },
  {
    slug: 'material-steel-2',
    category: 'materials-storage',
    kinds: ['staging'],
  },
  {
    slug: 'construction-debris-1',
    category: 'materials-storage',
    kinds: ['waste'],
  },
  {
    slug: 'construction-debris-2',
    category: 'materials-storage',
    kinds: ['waste'],
  },
  {
    slug: 'information-area',
    category: 'information',
    kinds: ['general_information_point'],
  },
  {
    slug: 'site-map',
    category: 'information',
    kinds: ['general_information_point'],
  },
  { slug: 'first-aid', category: 'emergency', kinds: ['medical_area'] },
  { slug: 'aed', category: 'emergency', kinds: ['medical_area'] },
  { slug: 'covid-checkpoint', category: 'emergency', kinds: ['medical_area'] },
  {
    slug: 'fire-extinguisher',
    category: 'emergency',
    kinds: ['fire_protection'],
  },
  { slug: 'muster-point', category: 'emergency', kinds: ['muster_point'] },
  // Planned: named and typed ahead of their art, drawn with the placeholder until it lands.
  { slug: 'exit-running', category: 'entrances', kinds: ['exit'] },
  {
    slug: 'cleanroom',
    category: 'ppe-offices',
    kinds: ['controlled_environment'],
  },
  {
    slug: 'icra-zone',
    category: 'ppe-offices',
    kinds: ['controlled_environment'],
  },
  {
    slug: 'negative-pressure',
    category: 'ppe-offices',
    kinds: ['controlled_environment'],
  },
  {
    slug: 'esd-zone',
    category: 'ppe-offices',
    kinds: ['controlled_environment'],
  },
  { slug: 'no-photography', category: 'entrances', kinds: ['site_rules'] },
  { slug: 'no-drones', category: 'entrances', kinds: ['site_rules'] },
  { slug: 'fall-hazard', category: 'emergency', kinds: ['hazard_zone'] },
  { slug: 'silica-dust', category: 'emergency', kinds: ['hazard_zone'] },
  { slug: 'noise', category: 'emergency', kinds: ['hazard_zone'] },
  { slug: 'overhead-load', category: 'emergency', kinds: ['hazard_zone'] },
  { slug: 'excavation', category: 'emergency', kinds: ['hazard_zone'] },
  { slug: 'hot-work', category: 'emergency', kinds: ['hazard_zone'] },
  { slug: 'confined-space', category: 'emergency', kinds: ['confined_space'] },
  { slug: 'arc-flash', category: 'emergency', kinds: ['energized_equipment'] },
  {
    slug: 'stop',
    category: 'deliveries-construction',
    kinds: ['traffic_control', 'restricted_area'],
  },
  {
    slug: 'yield',
    category: 'deliveries-construction',
    kinds: ['traffic_control'],
  },
  {
    slug: 'speed-limit',
    category: 'deliveries-construction',
    kinds: ['traffic_control'],
  },
  {
    slug: 'one-way',
    category: 'deliveries-construction',
    kinds: ['traffic_control'],
  },
  {
    slug: 'flagger',
    category: 'deliveries-construction',
    kinds: ['traffic_control'],
  },
  {
    slug: 'truck-crossing',
    category: 'deliveries-construction',
    kinds: ['traffic_control'],
  },
  {
    slug: 'fire-lane',
    category: 'deliveries-construction',
    kinds: ['traffic_control'],
  },
  {
    slug: 'concrete-washout',
    category: 'materials-storage',
    kinds: ['stormwater_erosion_control'],
  },
  {
    slug: 'storm-drain',
    category: 'materials-storage',
    kinds: ['stormwater_erosion_control'],
  },
  {
    slug: 'erosion-control',
    category: 'materials-storage',
    kinds: ['stormwater_erosion_control'],
  },
  {
    slug: 'wheel-wash',
    category: 'materials-storage',
    kinds: ['stormwater_erosion_control'],
  },
  {
    slug: 'spill-kit',
    category: 'materials-storage',
    kinds: ['stormwater_erosion_control'],
  },
  { slug: 'scaffold', category: 'conveyances', kinds: ['elevated_access'] },
  { slug: 'swing-stage', category: 'conveyances', kinds: ['elevated_access'] },
  { slug: 'mast-climber', category: 'conveyances', kinds: ['elevated_access'] },
  { slug: 'eyewash', category: 'emergency', kinds: ['medical_area'] },
  { slug: 'standpipe', category: 'emergency', kinds: ['fire_protection'] },
  { slug: 'fire-hydrant', category: 'emergency', kinds: ['fire_protection'] },
  {
    slug: 'alarm-pull-station',
    category: 'emergency',
    kinds: ['fire_protection'],
  },
  { slug: 'crane-zone', category: 'deliveries-construction', kinds: ['zone'] },
] as const satisfies readonly {
  slug: string
  category: PictogramCategoryId
  kinds: readonly string[]
}[]

export type PictogramSlug = (typeof PICTOGRAMS)[number]['slug']

export const PICTOGRAM_ICON_PREFIX = 'pictogram:'

const SLUGS: ReadonlySet<string> = new Set(PICTOGRAMS.map(p => p.slug))

export const toPictogramIconName = (slug: PictogramSlug) =>
  `${PICTOGRAM_ICON_PREFIX}${slug}` as const

export type PictogramIconName = ReturnType<typeof toPictogramIconName>

export const pictogramSlugOf = (
  iconName: string | null | undefined,
): PictogramSlug | null => {
  if (!iconName?.startsWith(PICTOGRAM_ICON_PREFIX)) {
    return null
  }
  const slug = iconName.slice(PICTOGRAM_ICON_PREFIX.length)
  return SLUGS.has(slug) ? (slug as PictogramSlug) : null
}
